import { storeInformationQueries } from "$/hooks/use-store-information"
import { STORE_TIER, STORE_TYPE, TranslationKey } from "@repo/common"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { z } from "zod"
import { ensureRequestedStoreSelected } from "./-utils/ensure-requested-store-selected"

const searchSchema = z.object({
  view: z
    .enum([
      "todos",
      "deliverys",
      "BikesInShop",
      "bikeExpecting",
      "readyForService",
      "collected",
      "proposalSent",
      "waitingForDelivery",
      "completed",
    ])
    .optional(),
  // Set by the admin app's "open in Alfred" links
  storeId: z.string().uuid().optional(),
})

export const Route = createFileRoute("/(app)/_auth/")({
  validateSearch: searchSchema,
  beforeLoad: async ({ search, preload, context: { queryClient } }) => {
    if (!search.storeId) return

    // Links preload on hover; only a real navigation may switch store.
    if (!preload) {
      await ensureRequestedStoreSelected(queryClient, search.storeId)
    }

    throw redirect({ to: "/", search: { view: search.view }, replace: true })
  },
  loaderDeps: ({ search }) => ({ view: search.view }),
  loader: async ({ deps, context: { queryClient } }) => {
    const storeInfo = await queryClient.ensureQueryData(
      storeInformationQueries.detail(queryClient),
    )
    const currentStore = storeInfo.stores.find(
      (store) => store.id === storeInfo.storeId,
    )
    if (currentStore?.type === STORE_TYPE.MOBILE) {
      throw redirect({
        to: "/event",
        replace: true,
      })
    }

    // The sidebar hides Översikt from free-tier stores (nav-business.tsx) —
    // Kalender is the first item they do see, so that's the fallback here,
    // matching this route's pre-existing default before it became the
    // post-login landing page.
    if (currentStore?.tier === STORE_TIER.FREE) {
      throw redirect({
        to: "/calendar",
        replace: true,
      })
    }

    // Translation keys; the breadcrumb renderer translates them
    const VIEW_LABELS = {
      todos: "alfred.overview.views.todos",
      deliverys: "alfred.overview.views.deliveries",
      BikesInShop: "alfred.overview.views.bikesInStore",
      bikeExpecting: "alfred.overview.views.bikeExpecting",
      readyForService: "alfred.overview.views.readyForService",
      collected: "alfred.overview.views.collected",
      proposalSent: "alfred.overview.views.proposalSent",
      waitingForDelivery: "alfred.overview.views.waitingForDelivery",
      completed: "alfred.overview.views.completed",
    } satisfies Record<NonNullable<typeof deps.view>, TranslationKey>

    // When a view is selected, return array of crumbs
    if (deps.view) {
      return {
        crumbs: ["alfred.nav.items.overview", VIEW_LABELS[deps.view]],
        from: "/(app)/_auth",
      }
    }

    // When on sidebar, return single crumb
    return {
      crumb: "alfred.nav.items.overview",
    }
  },
})
